import { useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { Button, Headline1, Meta, Spinner, theme } from 'aimo-ui';
import Info from 'components/common/InfoBox';
import AccountContext from 'context/AccountContext';
import { format } from 'date-fns';
import { READ_CUSTOMER_APPLICATION } from 'graphql/queries/readPermitApplication';
import useDynamicSheetSize from 'hooks/useDynamicSheetSize';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useCityParkingStore } from 'stores/cityParkingStore';
import styled from 'styled-components/native';
import { ColumnWithMargins, StyledWrapper } from 'styles/ContainerStyles';
import { MainWrapper } from 'styles/ModalStackContainerStyles';
import {
  Application,
  Maybe,
  Query,
  QueryReadPermitApplicationArgs,
} from 'types/generatedSchemaTypes';
import { CityParkingNavProps } from 'types/navigation';
import {
  CityViewScreenAction,
  getCityViewScreenAction,
  getCustomerAddress,
} from 'utils/cityParkingUtils';
import { currencyFormatter, getLocale } from 'utils/commonUtils';

const StyledContainer = styled(StyledWrapper)<{ color: string }>`
  background-color: ${(p) => p.color};
  border-radius: 8px;
  padding: 16px;
`;

const RenderButtons: React.FC<{
  action: CityViewScreenAction | undefined;
  testId: string;
  application: Maybe<Application> | undefined;
}> = ({ action, testId }) => {
  const { t } = useTranslation();
  const strongAuthFlow = useCityParkingStore(
    (state) => state.setStrongAuthFlow
  );
  const { navigate } = useNavigation<CityParkingNavProps>();
  switch (action) {
    case 'BUY_PERMIT': {
      return (
        <Button
          size="l"
          buttonType="primary"
          onPress={() => {
            navigate('purchase');
          }}
          text={t('button.buyPermit')}
          testID={`${testId}-buy-permit-button`}
        />
      );
    }
    //Appying is disabled for MVP
    case 'APPLY_PERMIT': {
      return <></>;
      // if (application && application.queueCustomerStatus !== 'DECLINED') {
      //   return (
      //     <Button
      //       disabled={application.queueCustomerStatus === 'OPEN'}
      //       size="l"
      //       buttonType="primary"
      //       onPress={() => {
      //         navigate('purchase');
      //       }}
      //       text={t('button.buyPermit')}
      //       testID={`${testId}-buy-permit-button`}
      //     />
      //   );
      // }
      // return (
      //   <Button
      //     size="l"
      //     buttonType="primary"
      //     onPress={() => navigate('apply')}
      //     text={t('button.applyPermit')}
      //     testID={`${testId}-apply-permit-button`}
      //   />
      // );
    }
    case 'IDENTIFY': {
      return (
        <Button
          size="l"
          buttonType="secondary"
          onPress={() => {
            strongAuthFlow(true);
          }}
          text={t('button.identify')}
          testID={`${testId}-identify-button`}
        />
      );
    }
    // Will be reimplemented on another another ticket
    // case 'REIDENTIFY': {
    //   return (
    //     <Button
    //       size="l"
    //       buttonType="tertiary"
    //       onPress={() => {
    //         strongAuthFlow(true);
    //       }}
    //       text={t('button.reIdentify')}
    //       testID={`${testId}-reidentify-button`}
    //     />
    //   );
    // }
    default:
      return null;
  }
};

const InfoBox: React.FC<{
  status: string;
  created: string;
}> = ({ status, created }) => {
  const { t } = useTranslation();
  switch (status) {
    case 'OPEN':
      return (
        <StyledContainer
          center
          mb={10}
          testID={'application-created'}
          color={theme.colors.lightBg.yellow}>
          <Meta>
            {t('cityPermits.applicationCreated', {
              applicationCreated: created,
            })}
          </Meta>
        </StyledContainer>
      );
    case 'ACCEPTED':
      return (
        <StyledContainer
          center
          mb={10}
          testID={'application-accepted'}
          color={theme.colors.lightBg.green}>
          <Meta>
            {t('cityPermits.applicationAccepted', {
              applicationCreated: created,
            })}
          </Meta>
        </StyledContainer>
      );

    case 'DECLINED':
      return (
        <StyledContainer
          center
          mb={10}
          testID={'application-declined'}
          color={theme.colors.lightBg.red}>
          <Meta>
            {t('cityPermits.applicationDeclined', {
              applicationCreated: created,
            })}
          </Meta>
        </StyledContainer>
      );
    default:
      return null;
  }
};

const CityParkingActionScreen = () => {
  const selectedItem = useCityParkingStore((state) => state.selectedPmc);
  const testId = 'street-parking-screen-selector';
  const { t } = useTranslation();
  const { customer } = useContext(AccountContext);
  const { contactEmail } = customer;
  const { handleLayout } = useDynamicSheetSize();
  const action = getCityViewScreenAction(customer);

  const { data, loading } = useQuery<Query, QueryReadPermitApplicationArgs>(
    READ_CUSTOMER_APPLICATION,
    {
      variables: {
        input: {
          customerGroupUid: selectedItem?.customerGroupUids?.[0] as string,
          email: contactEmail as string,
        },
      },
      fetchPolicy: 'network-only',
      skip: selectedItem?.customerGroupUids?.length === 0,
    }
  );
  const userApplication = React.useMemo(
    () => data?.readPermitApplication,
    [data]
  );

  let applicationCreated;
  if (userApplication) {
    applicationCreated = format(
      new Date(userApplication?.created ?? ''),
      'd.M.Y',
      {
        locale: getLocale(),
      }
    );
  }

  if (loading) {
    return (
      <StyledWrapper center>
        <Spinner />
      </StyledWrapper>
    );
  }

  const addressString = getCustomerAddress(customer.strongAuthentication);
  const priceString = (selectedItem?.prices?.[0].monthlyPrice ?? 0) / 100;
  //const userAddress = customer.strongAuthentication?.addresses?.[0];
  return (
    <MainWrapper
      noFlex
      testID={testId}
      onLayout={(e) => handleLayout(e.nativeEvent.layout)}>
      {userApplication && (
        <InfoBox
          status={userApplication.queueCustomerStatus as string}
          created={applicationCreated as string}
        />
      )}

      {action === 'APPLY_PERMIT' && (
        <Info
          bgColor={theme.colors.infoYellow}
          text={t('cityPermits.addressNotCoveredContactCS', {
            addressString,
          })}
        />
      )}
      {selectedItem && (
        <StyledWrapper center>
          <Headline1 center testID={`${testId}-price`}>
            {currencyFormatter(priceString, 'FI')}
          </Headline1>
          <ColumnWithMargins ml={20} mr={20} mt={10}>
            <Meta center={true} color={theme.colors.gray300}>
              {t('cityPermits.permitBillingInfo')}
            </Meta>
          </ColumnWithMargins>
        </StyledWrapper>
      )}
      {/* Will be implemented on an another ticket
      {screenName === 'REIDENTIFY' && (
        <>
          <StyledWrapper center mb={40} testID="reidentify-address">
            <Meta>
              <Headline3>Your home address is</Headline3>
              <Headline3
                color={
                  theme.colors.boost.green
                }>{` ${userAddress?.streetName} ${userAddress?.streetNumber}`}</Headline3>
            </Meta>
          </StyledWrapper>
          <StyledWrapper center mb={36}>
            <Meta color={theme.colors.gray300} center testID="reidentify-meta">
              {t('legalAddressAuth.reidentifyInfo')}
            </Meta>
          </StyledWrapper>
        </>
      )} */}
      <StyledWrapper>
        <RenderButtons
          action={action}
          testId={testId}
          application={userApplication}
        />
      </StyledWrapper>
    </MainWrapper>
  );
};

export default CityParkingActionScreen;
